import { useEffect, useState } from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import { Check, Close, Edit } from '@mui/icons-material';
import { AppIconButton } from '../../design-system';

interface GatewayInlineNameEditProps {
  name: string;
  serialNumber: string;
  onChange: (name: string) => void;
  disabled?: boolean;
}

/** Compact click-to-edit gateway name with serial shown as text. */
export function GatewayInlineNameEdit({
  name,
  serialNumber,
  onChange,
  disabled = false,
}: GatewayInlineNameEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  const startEdit = () => {
    if (disabled) return;
    setDraft(name);
    setEditing(true);
  };

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed) onChange(trimmed);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };

  return (
    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
      {editing ? (
        <Box display="flex" alignItems="center" gap={0.5}>
          <InputBase
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            autoFocus
            placeholder="Gateway name"
            sx={{
              typography: 'subtitle1',
              fontWeight: 600,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'primary.main',
              minWidth: 160,
              maxWidth: 280,
            }}
          />
          <AppIconButton size="small" color="primary" onClick={commit} aria-label="Apply name">
            <Check fontSize="small" />
          </AppIconButton>
          <AppIconButton size="small" onClick={cancel} aria-label="Cancel name edit">
            <Close fontSize="small" />
          </AppIconButton>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            onClick={startEdit}
            sx={{
              cursor: disabled ? 'default' : 'pointer',
              '&:hover': disabled ? undefined : { color: 'primary.main' },
            }}
          >
            {name.trim() || 'Unnamed gateway'}
          </Typography>
          {!disabled && (
            <AppIconButton size="small" onClick={startEdit} aria-label="Edit gateway name">
              <Edit sx={{ fontSize: 16 }} />
            </AppIconButton>
          )}
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {serialNumber}
      </Typography>
    </Box>
  );
}
